import { ShoppingCart, Mail, ArrowLeft, Shield, Zap, PoundSterling } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster } from "sonner";

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="bg-white border-b border-border p-6">
        <div className="max-w-2xl mx-auto">
          <a href="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </a>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-syne text-3xl font-bold text-primary tracking-tight">
                PriceSlice
              </h1>
              <p className="text-muted-foreground text-sm">Contact Us</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto p-6">
        {/* About Section */}
        <section className="mb-8">
          <h2 className="font-syne text-2xl font-bold text-primary mb-4">About PriceSlice</h2>
          <div className="prose prose-sm text-muted-foreground">
            <p className="mb-4">
              PriceSlice is your smart shopping companion for comparing grocery prices across UK supermarkets. 
              We help thousands of UK shoppers save money every week by finding the best deals at Tesco, 
              Sainsbury's, Asda, Morrisons, Aldi, Lidl, and more.
            </p>
            <p className="mb-4">
              Our mission is simple: <strong>help UK families stretch their grocery budget further</strong>. 
              With the cost of living rising, every penny counts. PriceSlice makes it easy to compare prices 
              in seconds and find the cheapest supermarket for your weekly shop.
            </p>
          </div>
        </section>

        {/* Features */}
        <section className="mb-8">
          <h3 className="font-syne text-xl font-bold text-primary mb-4">What We Offer</h3>
          <div className="grid gap-4">
            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-bold text-primary">Up-to-Date Price Comparison</h4>
                  <p className="text-sm text-muted-foreground">
                    Compare prices from Tesco, Sainsbury's, Asda, Morrisons, Aldi & more UK supermarkets. 
                    Our data is updated regularly to give you the most accurate prices.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <PoundSterling className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-bold text-primary">Total Basket Savings</h4>
                  <p className="text-sm text-muted-foreground">
                    See exactly how much you could save by shopping at different stores. 
                    We calculate your total basket cost across all major supermarkets.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-bold text-primary">Privacy First</h4>
                  <p className="text-sm text-muted-foreground">
                    No sign-up or personal data needed to compare prices — your shopping list 
                    stays on your device. Pro subscribers' payment details are handled securely 
                    by Stripe; see our <a href="/privacy" className="underline hover:text-primary">Privacy Policy</a> for details.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Contact Section */}
        <section className="mb-8">
          <h3 className="font-syne text-xl font-bold text-primary mb-4">Questions, Feedback, or Support</h3>
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center">
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Found a bug, got an idea, or just want to say hi? We read every message.
                  </p>
                  <a 
                    href="mailto:contactus@priceslice.uk?subject=PriceSlice%20feedback" 
                    className="text-xl font-bold text-green-700 hover:underline"
                  >
                    contactus@priceslice.uk
                  </a>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                We typically respond within 24-48 hours. For urgent matters, please include "URGENT" 
                in your subject line.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="text-center">
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
            <a href="/">Start Comparing Prices</a>
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            Free to use · No sign-up required · Unlimited comparisons (up to 5 items per list)
          </p>
        </section>

        {/* Footer */}
        <footer className="mt-12 pt-6 border-t border-border text-center">
          <p className="text-xs text-muted-foreground">
            © 2026 PriceSlice.uk · All rights reserved
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            <a href="/terms" className="hover:underline">Terms of Service</a>
            {" · "}
            <a href="/privacy" className="hover:underline">Privacy Policy</a>
          </p>
        </footer>
      </main>
    </div>
  );
};

export default ContactPage;
